import { GrindRestriction } from "../../interfaces/v2/grind-restriction";

export { GrindRestriction } from "../../interfaces/v2/grind-restriction";

// Domain codes shared with the backend. variant is mandatory (no wildcard);
// speed allows an Any wildcard that is exclusive per variant.
export const GRIND_VARIANT_REGULAR = 1;
export const GRIND_VARIANT_MYSTERY = 2;
export const GRIND_VARIANT_KNOCKOUT = 3;

export const GRIND_SPEED_ANY = 0;
export const GRIND_SPEED_SLOW = 1;
export const GRIND_SPEED_REGULAR = 2;
export const GRIND_SPEED_TURBO = 3;
export const GRIND_SPEED_HYPER_TURBO = 4;

export interface GrindOption {
  code: number;
  /** Untranslated label, or an i18n key when translate is true. */
  label: string;
  translate?: boolean;
}

export const GRIND_VARIANTS: readonly GrindOption[] = [
  { code: GRIND_VARIANT_KNOCKOUT, label: "Knockout" },
  { code: GRIND_VARIANT_MYSTERY, label: "Mystery" },
  { code: GRIND_VARIANT_REGULAR, label: "Regular" },
];

export const GRIND_SPEEDS: readonly GrindOption[] = [
  { code: GRIND_SPEED_ANY, label: "all_speeds", translate: true },
  { code: GRIND_SPEED_HYPER_TURBO, label: "Hyper-Turbo" },
  { code: GRIND_SPEED_TURBO, label: "Turbo" },
  { code: GRIND_SPEED_REGULAR, label: "Regular" },
  { code: GRIND_SPEED_SLOW, label: "Slow" },
];

const VARIANT_CODES: ReadonlySet<number> = new Set(
  GRIND_VARIANTS.map((option) => option.code)
);
const SPEED_CODES: ReadonlySet<number> = new Set(
  GRIND_SPEEDS.map((option) => option.code)
);

const VARIANT_TEXT_TO_CODE: ReadonlyMap<string, number> = new Map([
  ["Regular", GRIND_VARIANT_REGULAR],
  ["Mystery", GRIND_VARIANT_MYSTERY],
  ["Knockout", GRIND_VARIANT_KNOCKOUT],
]);

const SPEED_TEXT_TO_CODE: ReadonlyMap<string, number> = new Map([
  ["Slow", GRIND_SPEED_SLOW],
  ["Regular", GRIND_SPEED_REGULAR],
  ["Turbo", GRIND_SPEED_TURBO],
  ["Hyper-Turbo", GRIND_SPEED_HYPER_TURBO],
]);

/** Map a tournament variant to its code. Accepts the tournament text label or
 * an already-numeric code; returns null when unknown. */
export function grindVariantCodeFromText(value: unknown): number | null {
  if (typeof value === "number") {
    return VARIANT_CODES.has(value) ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const code = VARIANT_TEXT_TO_CODE.get(value);
  return code === undefined ? null : code;
}

/** Map a tournament speed to its code. Accepts the tournament text label or an
 * already-numeric code (never the Any wildcard, which tournaments do not have);
 * returns null when unknown. */
export function grindSpeedCodeFromText(value: unknown): number | null {
  if (typeof value === "number") {
    return value !== GRIND_SPEED_ANY && SPEED_CODES.has(value) ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }
  const code = SPEED_TEXT_TO_CODE.get(value);
  return code === undefined ? null : code;
}

export interface GrindRestrictionsView {
  rows: GrindRestriction[];
  ready: boolean;
  loadFailed: boolean;
  requestSeq: number;
}

export function emptyGrindRestrictionsView(): GrindRestrictionsView {
  return { rows: [], ready: false, loadFailed: false, requestSeq: 0 };
}

export function beginGrindRestrictionsLoad(
  view: GrindRestrictionsView
): GrindRestrictionsView {
  return {
    rows: [],
    ready: false,
    loadFailed: false,
    requestSeq: view.requestSeq + 1,
  };
}

export function applyGrindRestrictionsLoad(
  view: GrindRestrictionsView,
  requestSeq: number,
  payload: unknown
): GrindRestrictionsView {
  if (view.requestSeq !== requestSeq) {
    return view;
  }
  return {
    ...view,
    rows: parseGrindRestrictionsPayload(payload),
    ready: true,
    loadFailed: false,
  };
}

export function failGrindRestrictionsLoad(
  view: GrindRestrictionsView,
  requestSeq: number
): GrindRestrictionsView {
  if (view.requestSeq !== requestSeq) {
    return view;
  }
  return { ...view, rows: [], ready: false, loadFailed: true };
}

export function grindPairTaken(
  rows: { variant: number; speed: number }[],
  variant: number,
  speed: number,
  exceptIndex: number
): boolean {
  return rows.some(
    (row, index) =>
      index !== exceptIndex && row.variant === variant && row.speed === speed
  );
}

/** Speed options selectable for a variant at a given row, honoring the
 * Any-exclusivity rule: Any is offered only when the variant has no other
 * row, and specific speeds are hidden once the variant holds an Any row. */
export function grindSpeedDisabled(
  rows: { variant: number; speed: number }[],
  variant: number,
  speed: number,
  index: number
): boolean {
  if (grindPairTaken(rows, variant, speed, index)) {
    return true;
  }
  const others = rows.filter(
    (row, i) => i !== index && row.variant === variant
  );
  if (speed === GRIND_SPEED_ANY) {
    return others.length > 0;
  }
  return others.some((row) => row.speed === GRIND_SPEED_ANY);
}

function firstFreeSpeed(
  rows: { variant: number; speed: number }[],
  variant: number
): number | null {
  const taken = new Set(
    rows.filter((row) => row.variant === variant).map((row) => row.speed)
  );
  if (taken.size === 0) {
    return GRIND_SPEED_ANY;
  }
  if (taken.has(GRIND_SPEED_ANY)) {
    return null;
  }
  for (const option of GRIND_SPEEDS) {
    if (option.code !== GRIND_SPEED_ANY && !taken.has(option.code)) {
      return option.code;
    }
  }
  return null;
}

export function nextGrindRestriction(
  rows: { variant: number; speed: number }[]
): GrindRestriction | null {
  for (const variant of GRIND_VARIANTS) {
    const speed = firstFreeSpeed(rows, variant.code);
    if (speed !== null) {
      return { variant: variant.code, speed, min_current_blind: null };
    }
  }
  return null;
}

/** Enforce Any-exclusivity by substitution after the row at index changed:
 * setting Any drops the variant's other rows; setting a specific speed drops
 * that variant's Any row. Returns a new rows array. */
export function substituteGrindRestrictionConflicts(
  rows: GrindRestriction[],
  index: number
): GrindRestriction[] {
  const edited = rows[index];
  if (!edited) {
    return rows.slice();
  }
  return rows.filter((row, i) => {
    if (i === index || row.variant !== edited.variant) {
      return true;
    }
    if (edited.speed === GRIND_SPEED_ANY) {
      return false;
    }
    return row.speed !== GRIND_SPEED_ANY;
  });
}

export function grindRestrictionsValid(rows: GrindRestriction[]): boolean {
  const seen = new Set<string>();
  const hasAny = new Set<number>();
  const hasSpecific = new Set<number>();
  for (const row of rows) {
    if (!VARIANT_CODES.has(row.variant) || !SPEED_CODES.has(row.speed)) {
      return false;
    }
    if (
      typeof row.min_current_blind !== "number" ||
      !Number.isFinite(row.min_current_blind) ||
      row.min_current_blind < 0
    ) {
      return false;
    }
    const key = row.variant + "\0" + row.speed;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    if (row.speed === GRIND_SPEED_ANY) {
      hasAny.add(row.variant);
    } else {
      hasSpecific.add(row.variant);
    }
  }
  for (const variant of hasAny) {
    if (hasSpecific.has(variant)) {
      return false;
    }
  }
  return true;
}

function toGrindCode(value: unknown): number {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    return Number(value);
  }
  return NaN;
}

export function parseGrindRestrictionsPayload(data: unknown): GrindRestriction[] {
  if (data == null || typeof data !== "object") {
    throw new Error("invalid grind restrictions");
  }
  const restrictions = (data as { restrictions?: unknown }).restrictions;
  if (!Array.isArray(restrictions)) {
    throw new Error("invalid grind restrictions");
  }
  const rows: GrindRestriction[] = restrictions.map((row) => {
    if (row == null || typeof row !== "object") {
      throw new Error("invalid grind restrictions");
    }
    const raw = row as {
      variant?: unknown;
      speed?: unknown;
      min_current_blind?: unknown;
    };
    let min = raw.min_current_blind;
    if (typeof min === "string" && min.trim() !== "") {
      min = Number(min);
    }
    return {
      variant: toGrindCode(raw.variant),
      speed: toGrindCode(raw.speed),
      min_current_blind: min as number | null,
    };
  });
  if (!grindRestrictionsValid(rows)) {
    throw new Error("invalid grind restrictions");
  }
  return rows;
}

/**
 * PUT/204 bodies often omit restrictions. Keep the rows that were just saved
 * instead of treating a successful write as a failed parse.
 */
export function rowsFromGrindRestrictionsSave(
  data: unknown,
  fallback: GrindRestriction[]
): GrindRestriction[] {
  if (data == null || typeof data !== "object") {
    return fallback.slice();
  }
  const restrictions = (data as { restrictions?: unknown }).restrictions;
  if (!Array.isArray(restrictions)) {
    return fallback.slice();
  }
  return parseGrindRestrictionsPayload(data);
}

/** True when a save result still belongs to the open restrictions screen. */
export function shouldApplyGrindRestrictionsSaveResult(
  view: GrindRestrictionsView,
  requestSeq: number
): boolean {
  return view.requestSeq === requestSeq;
}

/**
 * Clear saving/spinner only for the latest save generation. An older save
 * finishing after reopen + a newer save must not unlock overlapping PUTs.
 */
export function shouldClearGrindRestrictionsSavingState(
  saveGen: number,
  currentSaveGen: number
): boolean {
  return saveGen === currentSaveGen;
}

/**
 * Commit a GET/PUT cache write only when the captured epoch still matches.
 * Plain GETs capture without bumping; saves bump on start (and again after a
 * successful write) so a concurrent GET cannot orphan or overwrite the save.
 */
export function shouldCommitGrindRestrictionsCache(
  writeEpoch: number,
  currentEpoch: number
): boolean {
  return writeEpoch === currentEpoch;
}

/**
 * A save owns the epoch from its start bump until it commits and advances,
 * or until it fails and drops ownership.
 */
export function grindRestrictionsSaveOwnsEpoch(
  saveEpoch: number | null,
  currentEpoch: number
): boolean {
  return saveEpoch != null && saveEpoch === currentEpoch;
}

/**
 * forceRefresh invalidates stale GET cache by bumping — except while a save
 * still owns the epoch. Bumping then orphans the PUT tap and lets a
 * concurrent GET cache pre-save rows for the TTL.
 */
export function nextGrindRestrictionsForceRefreshEpoch(
  currentEpoch: number,
  saveEpoch: number | null
): number {
  return grindRestrictionsSaveOwnsEpoch(saveEpoch, currentEpoch)
    ? currentEpoch
    : currentEpoch + 1;
}
